import * as React from 'react';
import { RouteComponentProps } from 'react-router';
import styled from 'styled-components';
import { animated } from 'react-spring';
import { useMutation } from '@apollo/client';

import { createLeadMutation } from '../../../../../client/__graphql__/mutations';
import { ILeadInput, ILeadResponse } from '../../../../__types__/global';

import { SiteContext } from '../../../../utils/context/SiteContext';
import { useForm } from '../../../../utils/hooks/useForm';
import { useFormTransition } from '../../../../utils/hooks/useFormTransition';

import { Section } from '../../../layout/Section';
import { Container } from '../../../layout/Container';
import { Thanks } from '../../../layout/Thanks';

import { Input } from '../../../ui/form/Input';
import { FormError } from '../../../ui/form/FormError';
import { Heading } from '../../../ui/text/Heading';
import { Paragraph } from '../../../ui/text/Paragraph';
import { Form, FormRow } from '../../../ui/form/Form';
import { ButtonGroup } from '../../../ui/button/ButtonGroup';
import { Button } from '../../../ui/button/Button';
import { ButtonLink } from '../../../ui/link/ButtonLink';
import { useFadeTransition } from '../../../../utils/hooks/useFadeTransition';
import { Seo } from '../../../../utils/seo/Index';

import { Hero } from '../../../ui/hero/Index';
import { Textarea } from '../../../ui/form/Textarea';

interface IRoute {
  office: string;
  path: string;
}

const SelgeEllerKjopeFritid: React.FC<RouteComponentProps<IRoute>> = ({
  location,
  match
}) => {
  const { fadeIn: pageFadeIn } = useFadeTransition({
    immidiate: true
  });

  return (
    <>
      <Seo
        title="Selge eller kjøpe fritidseiendom - PrivatMegleren"
        description="Når det kommer til det mest verdifulle du eier, skal du ha høye forventninger"
        url={location.pathname}
      />
      <Hero
        imageUrl="https://cdn.reeltime.no/pm_assets/kampanjer/kristiandsand/Bilde_sedberg.jpg"
        opacity={0.4}
        className="content-height flex padding-top-50"
        alt="bilde"
      >
        <HeroContent style={pageFadeIn}>
          <Heading tag="h1" center={true}>
            Vurderer du å selge eller kjøpe fritidseiendom?
          </Heading>
          <Paragraph center={true}>
            Privatmegleren Sædberg og Lian er nå godt i gang med å planlegge
            sesongen for salg av hytter og leiligheter ved sjøen som skal ut for
            salg i år.
          </Paragraph>
        </HeroContent>
      </Hero>
      <Container style={pageFadeIn}>
        <Section>
          <Paragraph style={{ marginTop: '30px' }} center={true}>
            Vår oppgave er å oppnå resultater for deg når du er i en situasjon
            der mye står på spill. Om du skal kjøpe eller selge, hjelper vi deg
            med strategi, taktisk planlegging og gjennomføring. Med meglere
            håndplukket blant de beste i markedet og innovative bransjeledende
            løsninger, gir vi deg et fortrinn i eiendomssalget.
          </Paragraph>
          <Paragraph center={true}>
            Vårt mål er å gi våre kunder en opplevelse utover det som forventes,
            og å oppfylle drømmen om fritidseiendom. Hele 9 av 10 kunder velger
            oss på anbefaling fra tidligere kunder, og er en bekreftelse på at
            våre meglere leverer en kundeopplevelse og resultat som overgår dine
            forventninger.
          </Paragraph>
          <Paragraph center={true}>
            Privatmegleren Sædberg og Lian er spesialister for fritidseiendommer
            ved sjøen på Sørlandet. Vårt hytteteam har lang erfaring med salg av
            eiendommer og vårt distrikt for salg av fritidseiendommer er
            Grimstad, Lillesand, Kristiansand, Søgne, Mandal, Lindesnes, Farsund
            og Flekkefjord.
          </Paragraph>
        </Section>
        <SelgeForm />
        <KjopeForm />
      </Container>
    </>
  );
};

const FormWrapper = styled(animated.div)``;

const KjopeForm: React.FC = () => {
  const { routeConfig }: any = React.useContext(SiteContext);
  const [posted, setPosted] = React.useState(false);

  const { fadeIn, fadeOut } = useFormTransition({
    posted
  });

  const MAIL_MUTATION = createLeadMutation({
    mail: {
      response: `{
            success
          }`
    }
  });

  const [mail, { loading, error }] = useMutation<ILeadResponse, ILeadInput>(
    MAIL_MUTATION,
    {
      onCompleted: ({ lead }) => {
        if (lead.mail?.success) {
          setPosted(true);
        }
      }
    }
  );

  const { fields, getFields, handleSubmit, handleChange } = useForm({
    fields: [
      {
        type: 'text',
        name: 'name',
        placeholder: 'Eks. Ola Nordmann',
        value: '',
        label: 'Navn *',
        required: true
      },
      {
        type: 'tel',
        name: 'phone',
        placeholder: 'Eks. 00000000',
        value: '',
        label: 'Telefon *',
        labelAfter: '(Brukes til å kontakte deg)',
        required: true
      },
      {
        type: 'email',
        name: 'email',
        placeholder: 'Eks. ole@mail.no',
        value: '',
        label: 'E-post *',
        labelAfter: '(Brukes til å kontakte deg)',
        required: true
      },
      {
        type: 'textarea',
        name: 'comment',
        placeholder: 'Skriv inn hvilket område og prisklasse som er aktuelt',
        value: '',
        label: 'Kommentar *',
        required: true
      }
    ],
    submit: () => {
      const preparedFields: any = getFields(fields);
      mail({
        variables: {
          input: {
            ...preparedFields,
            zip: '0000',
            referer: location.pathname,
            leadType: 'EPOST',
            mailType: 10
          }
        }
      });
    }
  });

  return (
    <>
      <FormWrapper style={fadeOut}>
        <Heading tag="h2" center={true}>
          Kjøpe fritidseiendom?
        </Heading>
        <Form
          onSubmit={handleSubmit}
          noValidate
          style={{ paddingBottom: '50px' }}
        >
          {error &&
            error.graphQLErrors.map((error, index) => (
              <FormError key={`error_${index}`} message={error.message} />
            ))}
          {fields
            .filter((item) => item?.type !== 'textarea')
            .map((item, index) => {
              return (
                <FormRow key={`field_${item.name}`}>
                  <Input
                    type={item.type}
                    name={item.name}
                    label={item.label}
                    labelAfter={item.labelAfter}
                    placeholder={item.placeholder}
                    value={item.value}
                    onChange={handleChange}
                    error={item.error}
                  />
                </FormRow>
              );
            })}
          {fields
            .filter((item) => item?.type === 'textarea')
            .map((item) => (
              <FormRow key={`field_${item.name}`}>
                <Textarea
                  name={item?.name}
                  label={item?.label}
                  labelAfter={item?.labelAfter}
                  placeholder={item?.placeholder}
                  value={item?.value}
                  onChange={handleChange}
                  error={item?.error}
                />
              </FormRow>
            ))}
          <ButtonGroup>
            <Button
              type="primary"
              colorTheme="gold"
              disabled={loading}
              loading={loading}
            >
              Kontakt meg
            </Button>
            <ButtonLink
              as="a"
              href={routeConfig.personvern.url}
              className="privacy-link-mobile"
            >
              Personvernpolicy
            </ButtonLink>
          </ButtonGroup>
        </Form>
      </FormWrapper>
      <Thanks style={fadeIn}>
        <Section className="form-section">
          <Heading tag="h1" center={true}>
            Tusen takk
          </Heading>
          <Paragraph center={true}>
            En eiendomsmegler vil ta kontakt med deg så snart som mulig.
          </Paragraph>
        </Section>
      </Thanks>
    </>
  );
};

const SelgeForm: React.FC = () => {
  const { routeConfig }: any = React.useContext(SiteContext);
  const [posted, setPosted] = React.useState(false);

  const { fadeIn, fadeOut } = useFormTransition({
    posted
  });

  const MAIL_MUTATION = createLeadMutation({
    mail: {
      response: `{
              success
            }`
    }
  });

  const [mail, { loading, error }] = useMutation<ILeadResponse, ILeadInput>(
    MAIL_MUTATION,
    {
      onCompleted: ({ lead }) => {
        if (lead.mail?.success) {
          setPosted(true);
        }
      }
    }
  );

  const { fields, getFields, handleSubmit, handleChange } = useForm({
    fields: [
      {
        type: 'text',
        name: 'name',
        placeholder: 'Eks. Ola Nordmann',
        value: '',
        label: 'Navn *',
        required: true
      },
      {
        type: 'tel',
        name: 'phone',
        placeholder: 'Eks. 00000000',
        value: '',
        label: 'Telefon *',
        labelAfter: '(Brukes til å kontakte deg)',
        required: true
      },
      {
        type: 'email',
        name: 'email',
        placeholder: 'Eks. ole@mail.no',
        value: '',
        label: 'E-post *',
        labelAfter: '(Brukes til å kontakte deg)',
        required: true
      },
      {
        type: 'text',
        name: 'address',
        placeholder: 'Eks. Adresseveien 23',
        value: '',
        label: 'Adresse',
        required: true
      }
    ],
    submit: () => {
      const preparedFields: any = getFields(fields);
      mail({
        variables: {
          input: {
            ...preparedFields,
            zip: '0000',
            referer: location.pathname,
            leadType: 'EPOST',
            mailType: 9
          }
        }
      });
    }
  });

  return (
    <>
      <FormWrapper style={fadeOut}>
        <Heading tag="h2" center={true}>
          Selge fritidseiendom?
        </Heading>
        <Form
          onSubmit={handleSubmit}
          noValidate
          style={{ paddingBottom: '50px' }}
        >
          {error &&
            error.graphQLErrors.map((error, index) => (
              <FormError key={`error_${index}`} message={error.message} />
            ))}
          {fields.map((item, index) => {
            return (
              <FormRow key={`field_${index}`}>
                <Input
                  type={item.type}
                  name={item.name}
                  label={item.label}
                  labelAfter={item.labelAfter}
                  placeholder={item.placeholder}
                  value={item.value}
                  onChange={handleChange}
                  error={item.error}
                />
              </FormRow>
            );
          })}
          <ButtonGroup>
            <Button
              type="primary"
              colorTheme="gold"
              disabled={loading}
              loading={loading}
            >
              Kontakt meg
            </Button>
            <ButtonLink
              as="a"
              href={routeConfig.personvern.url}
              className="privacy-link-mobile"
            >
              Personvernpolicy
            </ButtonLink>
          </ButtonGroup>
        </Form>
      </FormWrapper>
      <Thanks style={fadeIn}>
        <Section className="form-section">
          <Heading tag="h1" center={true}>
            Tusen takk
          </Heading>
          <Paragraph center={true}>
            En eiendomsmegler vil ta kontakt med deg så snart som mulig.
          </Paragraph>
        </Section>
      </Thanks>
    </>
  );
};

export default SelgeEllerKjopeFritid;

const HeroContent = styled(animated.div)`
  display: flex;
  flex-flow: column;
  align-items: center;
  width: 100%;
  max-width: 960px;
  padding: 0 30px;

  h1 {
    margin-bottom: 0;
    width: 100%;
    word-break: break-word;
  }

  h2 {
    width: 100%;
    word-break: break-word;
  }
`;

const StyledTop = styled(animated.div)`
  margin-top: 66px;
  width: 100%;
  overflow: hidden;
  margin-bottom: 3em;

  img {
    width: 100%;
    max-height: 500px;
    object-fit: contain;
    object-position: center;
  }

  .video-mobil {
    display: block;
  }
  .video-desktop {
    display: none;
  }

  @media (min-width: 600px) {
    .video-mobil {
      display: none;
    }
    .video-desktop {
      display: block;
    }
  }

  @media all and (max-width: 1280px) {
    img {
      min-height: 300px;
      object-fit: cover;
    }
  }
`;
